// web-frontend/src/tutorial/TutorialContext.jsx
// Single source of truth for the tutorial layer. Wraps MainLayout.
//
// Surface area (consumed by HomePage / AppSettingsPage):
//   const { startTutorial, replay, finish, isActive, ... } = useTutorial();
//   startTutorial(steps)  -- start with a resolved step list
//   replay(user)          -- clear localStorage flag and start with steps for user
//   finish({ skipped })   -- mark done and close
// TutorialOverlay (mounted next to the provider) reads state directly.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import storage from './storage';
import { resolveStepsForUser } from './steps';
import logger from '../shared/utils/logger';

const TutorialContext = createContext(null);

const MOBILE_BREAKPOINT = 1024; // sidebar is fixed visible at >= 1024 in MainLayout.css

function detectIsMobile() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT;
}

function detectIsSidebarOpen() {
  if (typeof document === 'undefined') return false;
  // Desktop layout: sidebar is always visible (not relying on `.mobile-open`).
  if (window.innerWidth >= MOBILE_BREAKPOINT) return true;
  const sidebar = document.querySelector('.sidebar');
  return !!(sidebar && sidebar.classList.contains('mobile-open'));
}

export function TutorialProvider({ children, userId, user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(detectIsMobile());
  const [isSidebarOpen, setIsSidebarOpen] = useState(detectIsSidebarOpen());

  // Track mounted state to avoid setState after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ---- Viewport tracking -------------------------------------------------
  useEffect(() => {
    const onResize = () => {
      if (!mountedRef.current) return;
      setIsMobile(detectIsMobile());
      setIsSidebarOpen(detectIsSidebarOpen());
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // ---- Sidebar open/close tracking via class observer --------------------
  // The MainLayout sidebar toggles a `mobile-open` class on `.sidebar`.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    let attached = false;
    let observer = null;

    const attach = () => {
      const sidebar = document.querySelector('.sidebar');
      if (!sidebar) return false;
      observer = new MutationObserver(() => {
        if (!mountedRef.current) return;
        setIsSidebarOpen(detectIsSidebarOpen());
      });
      observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
      attached = true;
      return true;
    };

    if (!attach()) {
      // Sidebar not yet mounted; retry once microtask ahead.
      const retryId = window.setTimeout(attach, 100);
      return () => {
        window.clearTimeout(retryId);
        if (observer) observer.disconnect();
      };
    }

    return () => {
      if (observer) observer.disconnect();
      attached = false;
    };
  }, []);

  // ---- Lifecycle ---------------------------------------------------------
  const startTutorial = useCallback(
    (resolvedSteps) => {
      if (!Array.isArray(resolvedSteps) || resolvedSteps.length === 0) return;
      logger?.debug && logger.debug('[Tutorial] start', {
        count: resolvedSteps.length, userId,
      });
      setSteps(resolvedSteps);
      // Restore from session ONLY if it belonged to the same userId.
      // A bare integer (legacy format) or a different userId is treated as
      // stale, so we always begin at step 0 — this ensures step 0 (language
      // picker) is shown to every new login.
      const restored = storage.loadSessionStepIndex(userId);
      const initial = restored != null && restored < resolvedSteps.length ? restored : 0;
      setStepIndex(initial);
      storage.saveSessionStepIndex(initial, userId);
      setIsActive(true);

      // If a step has ensureRoute, route there.
      // 🔥 [2026-07-13] 단, 세션 복원(리로드) 시에는 강제 이동하지 않음 — 매칭 스텝에서 멈춘 채
      //   새로고침할 때마다 /matching 으로 끌려가 "홈으로 못 나간다"고 느끼던 문제의 재이동 경로 차단.
      const first = resolvedSteps[initial];
      if (restored == null && first && first.ensureRoute && location.pathname !== first.ensureRoute) {
        navigate(first.ensureRoute);
      }
    },
    [navigate, location.pathname, userId],
  );

  // 🔥 [2026-07-13] 라우트가 바뀌면 MainLayout(=이 Provider)이 리마운트되며 진행 중이던 투어가
  //   통째로 사라지던 문제 — 기업/바이어 매칭 투어가 '/matching 이동' 스텝에서 끊기던 근본 원인.
  //   → 마운트 시 세션에 진행 중 스텝 인덱스(>0)가 남아 있으면 자동 재개.
  //   finish/skip 은 세션 인덱스를 지우므로 종료된 투어는 재개되지 않는다.
  //   (startTutorial 의 복원 경로가 저장 인덱스에서 이어가고, 복원 시 ensureRoute 는 건너뜀.)
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || isActive) return;
    if (!user || !userId) return;
    const saved = storage.loadSessionStepIndex(userId);
    if (saved == null || saved <= 0) return;
    const resolved = resolveStepsForUser(user);
    if (Array.isArray(resolved) && resolved.length > saved) {
      resumedRef.current = true;
      logger?.debug && logger.debug('[Tutorial] resume after remount', { saved });
      startTutorial(resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userId, isActive]);

  const advance = useCallback(() => {
    setStepIndex((current) => {
      const next = current + 1;
      if (next >= steps.length) {
        // End of tutorial — defer finish() to the next tick to avoid mutating
        // state while inside a setState updater.
        if (userId) {
          window.setTimeout(() => {
            storage.markDone(userId, { skipped: false });
            storage.clearSessionStepIndex();
            if (mountedRef.current) {
              setIsActive(false);
              setStepIndex(0);
              setSteps([]);
            }
          }, 0);
        }
        return current;
      }
      storage.saveSessionStepIndex(next, userId);
      const step = steps[next];
      if (step && step.ensureRoute && location.pathname !== step.ensureRoute) {
        navigate(step.ensureRoute);
      }
      return next;
    });
  }, [steps, navigate, location.pathname, userId]);

  const goBack = useCallback(() => {
    setStepIndex((current) => {
      const prev = Math.max(0, current - 1);
      storage.saveSessionStepIndex(prev, userId);
      return prev;
    });
  }, [userId]);

  const finish = useCallback(
    ({ skipped = false } = {}) => {
      logger?.debug && logger.debug('[Tutorial] finish', { skipped });
      if (userId) storage.markDone(userId, { skipped });
      storage.clearSessionStepIndex();
      if (!mountedRef.current) return;
      setIsActive(false);
      setStepIndex(0);
      setSteps([]);
    },
    [userId],
  );

  const replay = useCallback(
    (resolvedSteps) => {
      if (userId) storage.reset(userId);
      storage.clearSessionStepIndex();
      startTutorial(resolvedSteps);
    },
    [userId, startTutorial],
  );

  // Auto-finish when stepIndex reaches the end of steps via internal advance.
  useEffect(() => {
    if (!isActive) return;
    if (steps.length === 0) return;
    if (stepIndex >= steps.length) {
      finish({ skipped: false });
    }
  }, [isActive, steps.length, stepIndex, finish]);

  // ---- API ---------------------------------------------------------------
  const value = useMemo(
    () => ({
      // state
      isActive,
      steps,
      stepIndex,
      isMobile,
      isSidebarOpen,
      // controls
      startTutorial,
      advance,
      goBack,
      finish,
      replay,
      // navigation helper used by CTA actions
      navigate,
    }),
    [
      isActive,
      steps,
      stepIndex,
      isMobile,
      isSidebarOpen,
      startTutorial,
      advance,
      goBack,
      finish,
      replay,
      navigate,
    ],
  );

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    // Returning a no-op stub keeps consumers from crashing when the provider
    // is intentionally absent (e.g. auth pages outside MainLayout).
    return {
      isActive: false,
      steps: [],
      stepIndex: 0,
      isMobile: false,
      isSidebarOpen: true,
      startTutorial: () => {},
      advance: () => {},
      goBack: () => {},
      finish: () => {},
      replay: () => {},
      navigate: () => {},
    };
  }
  return ctx;
}

export default TutorialContext;
